import React, { useState, useEffect, useCallback } from 'react';
import { invoiceApi, formatApiErrorDetail } from '../../lib/api';
import { formatCurrency, formatDate, getStatusColor } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Pagination } from '../../components/Pagination';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Receipt,
  Eye,
  Loader2,
  Download,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

applyPlugin(jsPDF);

export default function ShopOwnerInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({ total: 0, pages: 0 });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await invoiceApi.list(params);
      setInvoices(data.items || data);
      setPaginationData({ total: data.total || 0, pages: data.pages || 0 });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredInvoices = invoices;

  // Calculate outstanding total
  const outstandingTotal = invoices
    .filter(inv => ['GENERATED', 'PARTIAL', 'OVERDUE'].includes(inv.status))
    .reduce((sum, inv) => {
      const paid = inv.payments?.reduce((p, pay) => p + pay.amount_paid, 0) || 0;
      return sum + (inv.total_amount - paid);
    }, 0);

  const downloadPDF = (invoice) => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const fmtAmt = (n) => 'Rs.' + Number(n).toLocaleString('en-IN', {maximumFractionDigits: 0});
    
    doc.setFillColor(0, 93, 232);
    doc.rect(0, 0, pageW, 38, 'F');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('MOBIKOO', 20, 16);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text('2228, EFC Business Center, JK Infotech,', 20, 23);
    doc.text('Hinjewadi Phase 1, Pune, Maharashtra, 411057', 20, 28);
    doc.text('GSTIN: 27ENGPA3125K1Z5 | Mobile: 8600862333', 20, 33);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('TAX INVOICE', pageW - 20, 16, { align: 'right' });
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(invoice.invoice_number, pageW - 20, 24, { align: 'right' });
    doc.setFontSize(8);
    doc.text('Date: ' + formatDate(invoice.generated_at), pageW - 20, 31, { align: 'right' });
    
    doc.setTextColor(100); doc.setFontSize(8);
    doc.text('BILL TO', 20, 50);
    doc.setTextColor(0); doc.setFontSize(11); doc.setFont(undefined, 'bold');
    doc.text(invoice.tenant?.company_name || 'N/A', 20, 57);
    doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(60);
    let billY = 63;
    if (invoice.tenant?.address) { const lines = doc.splitTextToSize(invoice.tenant.address, 75); doc.text(lines, 20, billY); billY += lines.length * 5 + 3; }
    if (invoice.tenant?.phone) { doc.text('Mobile: ' + invoice.tenant.phone, 20, billY); billY += 5; }
    if (invoice.tenant?.gstin) { doc.text('GSTIN: ' + invoice.tenant.gstin, 20, billY); billY += 5; }
    
    doc.setTextColor(100); doc.setFontSize(8);
    doc.text('INVOICE DETAILS', pageW - 80, 50);
    doc.setTextColor(0); doc.setFontSize(9);
    doc.setTextColor(100); doc.text('Period:', pageW - 80, 57);
    doc.setTextColor(0); doc.setFont(undefined, 'bold');
    doc.text(formatDate(invoice.billing_period_start) + ' to ' + formatDate(invoice.billing_period_end), pageW - 55, 57);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100); doc.text('Status:', pageW - 80, 63);
    doc.setTextColor(0); doc.setFont(undefined, 'bold'); doc.text(invoice.status, pageW - 55, 63);
    doc.setFont(undefined, 'normal');
    
    doc.setDrawColor(220); doc.line(20, 82, pageW - 20, 82);
    
    const clubbed = {};
    (invoice.line_items || []).forEach(item => {
      const key = item.branch_name + '__' + item.unit_price;
      if (clubbed[key]) { clubbed[key].warranty_count += item.warranty_count; clubbed[key].line_total += item.line_total; }
      else { clubbed[key] = { ...item }; }
    });
    const tableData = Object.values(clubbed).map(item => [
      item.branch_name, item.plan_name, item.hsn_code || '999799',
      String(item.warranty_count), fmtAmt(item.unit_price), fmtAmt(item.line_total),
    ]);
    doc.autoTable({
      startY: 87,
      head: [['Branch', 'Description', 'HSN', 'Qty', 'Unit Price', 'Total']],
      body: tableData, theme: 'grid',
      headStyles: { fillColor: [0, 93, 232], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
      bodyStyles: { fontSize: 8.5 },
      columnStyles: { 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
      margin: { left: 20, right: 20 },
    });
    
    let finalY = doc.lastAutoTable.finalY + 8;
    const cgstRate = invoice.cgst_rate || 9;
    const sgstRate = invoice.sgst_rate || 9;
    const cgstAmt = invoice.cgst_amount || (invoice.subtotal * cgstRate / 100);
    const sgstAmt = invoice.sgst_amount || (invoice.subtotal * sgstRate / 100);
    const totalTax = invoice.total_tax || (cgstAmt + sgstAmt);
    const grandTotal = invoice.total_amount || (invoice.subtotal + totalTax);
    
    const totalsX = pageW - 90;
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(totalsX - 5, finalY - 3, 75, 48, 3, 3, 'F');
    doc.setFontSize(9);
    [['Subtotal:', fmtAmt(invoice.subtotal)], ['CGST (' + cgstRate + '%):', fmtAmt(cgstAmt)], ['SGST (' + sgstRate + '%):', fmtAmt(sgstAmt)], ['Total Tax:', fmtAmt(totalTax)]].forEach(([label, val], i) => {
      doc.setTextColor(100); doc.text(label, totalsX, finalY + 4 + i * 6);
      doc.setTextColor(0); doc.text(val, pageW - 22, finalY + 4 + i * 6, { align: 'right' });
    });
    doc.setDrawColor(0, 93, 232); doc.line(totalsX - 3, finalY + 29, pageW - 18, finalY + 29);
    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(0, 93, 232);
    doc.text('Grand Total:', totalsX, finalY + 39);
    doc.text(fmtAmt(grandTotal), pageW - 22, finalY + 39, { align: 'right' });
    finalY += 55;
    
    if (invoice.payments?.length > 0) {
      doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(0);
      doc.text('Payment History', 20, finalY);
      doc.autoTable({ startY: finalY + 4, head: [['Date', 'Method', 'Amount', 'Reference']], body: invoice.payments.map(p => [formatDate(p.payment_date), p.payment_method, fmtAmt(p.amount_paid), p.transaction_reference || '-']), theme: 'plain', headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontSize: 8 }, bodyStyles: { fontSize: 8 }, margin: { left: 20, right: 20 } });
      finalY = doc.lastAutoTable.finalY + 8;
    }
    
    const termsY = Math.min(finalY + 5, pageH - 65);
    doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(0);
    doc.text('Terms & Conditions', 20, termsY);
    doc.setFont(undefined, 'normal'); doc.setFontSize(7.5); doc.setTextColor(80);
    ['A. Resellers are responsible for sharing all customers data with Mobikoo on same day.', 'B. Warranty plans once activated are non-cancellable and non-refundable.', 'C. This is a computer-generated invoice and does not require a sign or stamp.', 'D. Subject to Pune, Maharashtra jurisdiction.'].forEach((t, i) => { doc.text(t, 20, termsY + 5 + i * 4, { maxWidth: pageW - 40 }); });
    
    doc.setDrawColor(220); doc.line(20, pageH - 12, pageW - 20, pageH - 12);
    doc.setFontSize(7); doc.setTextColor(150);
    doc.text('Mobikoo | 2228, EFC Business Center, JK Infotech, Hinjewadi Phase 1, Pune 411057 | GSTIN: 27ENGPA3125K1Z5', pageW / 2, pageH - 7, { align: 'center' });
    
    doc.save('Invoice-' + invoice.invoice_number + '.pdf');
    toast.success('Invoice PDF downloaded');
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Invoices
          </h1>
          <p className="text-slate-600 mt-1">View and download your invoices</p>
        </div>

        {/* Outstanding Summary */}
        {outstandingTotal > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-amber-700">Outstanding Amount</p>
                  <p className="text-2xl font-bold text-amber-800">{formatCurrency(outstandingTotal)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filter */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="GENERATED">Generated</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
                <SelectItem value="PARTIAL">Partial</SelectItem>
                <SelectItem value="OVERDUE">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : filteredInvoices.length > 0 ? (
              <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} className="table-row-hover">
                      <TableCell className="font-medium text-slate-900">
                        {invoice.invoice_number}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(invoice.billing_period_start)} - {formatDate(invoice.billing_period_end)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(invoice.total_amount)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(invoice.status)} border`}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(invoice.generated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              const { data } = await invoiceApi.get(invoice.id);
                              setSelectedInvoice(data);
                              setShowViewDialog(true);
                            }}
                            data-testid={`view-invoice-${invoice.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              const { data } = await invoiceApi.get(invoice.id);
                              downloadPDF(data);
                            }}
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={page} pages={paginationData.pages} total={paginationData.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No invoices found</h3>
                <p className="text-slate-500 mt-1">Your invoices will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Invoice Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                Invoice {selectedInvoice?.invoice_number}
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={`${getStatusColor(selectedInvoice.status)} border`}>
                      {selectedInvoice.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Billing Period</p>
                    <p className="font-medium">
                      {formatDate(selectedInvoice.billing_period_start)} -{' '}
                      {formatDate(selectedInvoice.billing_period_end)}
                    </p>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <h4 className="font-medium mb-2">Line Items</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Branch</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>HSN</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedInvoice.line_items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.branch_name}</TableCell>
                          <TableCell>{item.plan_name}</TableCell>
                          <TableCell>{item.hsn_code || '999799'}</TableCell>
                          <TableCell className="text-right">{item.warranty_count}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.line_total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-slate-600">Subtotal</span>
                    <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-600">CGST ({selectedInvoice.cgst_rate || 9}%)</span>
                    <span>{formatCurrency(selectedInvoice.cgst_amount || selectedInvoice.subtotal * 0.09)}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-600">SGST ({selectedInvoice.sgst_rate || 9}%)</span>
                    <span>{formatCurrency(selectedInvoice.sgst_amount || selectedInvoice.subtotal * 0.09)}</span>
                  </div>
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-slate-500">Total Tax</span>
                    <span className="text-slate-500">{formatCurrency(selectedInvoice.total_tax || selectedInvoice.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-lg border-t pt-2">
                    <span>Grand Total</span>
                    <span>{formatCurrency(selectedInvoice.total_amount)}</span>
                  </div>
                </div>

                {/* Payments */}
                {selectedInvoice.payments?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Payment History</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Reference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedInvoice.payments.map((payment, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{formatDate(payment.payment_date)}</TableCell>
                            <TableCell>{payment.payment_method}</TableCell>
                            <TableCell className="text-right">{formatCurrency(payment.amount_paid)}</TableCell>
                            <TableCell>{payment.transaction_reference || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => downloadPDF(selectedInvoice)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
